import { Head, useForm } from "@inertiajs/react";
import { LoaderCircle, Upload } from "lucide-react";
import type { FormEventHandler } from "react";
import Dropzone from "shadcn-dropzone";

import InputError from "@/components/input-error";
import ProductTemplateBanner from "@/components/product-template-banner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import AppLayout from "@/layouts/app-layout";
import { productsPath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Productos",
    href: productsPath(),
  },
  {
    title: "Importar",
    href: "/products/import",
  },
];

export default function Import() {
  const { data, setData, post, processing, errors, reset } = useForm({
    file: null as File | null,
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    if (!data.file) {
      return;
    }

    post("/products/import", {
      forceFormData: true,
      onFinish: () => reset("file"),
    });
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0] || null;
    setData("file", file);
  };

  const handleFileRemove = () => {
    setData("file", null);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Importar Productos" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Importar Productos</h2>
          <p className="text-muted-foreground">
            Cargar archivo Excel o CSV para importar productos al catálogo
          </p>
        </div>

        <ProductTemplateBanner />

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Cargar Archivo de Productos</CardTitle>
              <CardDescription>
                Selecciona un archivo Excel (.xlsx, .xls) o CSV (.csv) con los
                productos a importar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-6" onSubmit={submit}>
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">
                        Formato del archivo
                      </h4>
                      <p className="text-sm text-blue-800 mb-3">
                        El archivo debe contener las siguientes columnas:
                      </p>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>
                          • <strong>Codigo Material</strong> (opcional) - Código
                          interno del producto
                        </li>
                        <li>
                          • <strong>Descripcion</strong> (requerido) - Nombre o
                          descripción del producto
                        </li>
                        <li>
                          • <strong>Categoría</strong> (opcional) - Categoría
                          principal del producto
                        </li>
                        <li>
                          • <strong>Subcategoría</strong> (opcional) -
                          Subcategoría del producto
                        </li>
                      </ul>
                      <p className="text-xs text-blue-700 mt-3">
                        * Las columnas pueden tener nombres similares. El
                        sistema intentará mapearlas automáticamente.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <div className="w-full">
                      <Dropzone
                        onDrop={handleFileChange}
                        accept={ACCEPTED_FILE_TYPES.EXCEL}
                        maxFiles={1}
                        disabled={processing}
                      >
                        {() => (
                          <div className="flex justify-center items-center w-full h-32 border-dashed border-2 border-gray-200 rounded-lg hover:bg-accent hover:text-accent-foreground transition-all select-none cursor-pointer">
                            <div className="flex items-center flex-col gap-1.5">
                              <div className="flex items-center flex-row gap-0.5 text-sm font-medium">
                                <Upload className="mr-2 h-4 w-4" />
                                Subir archivo
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Arrastra y suelta o haz clic para seleccionar
                              </p>
                            </div>
                          </div>
                        )}
                      </Dropzone>

                      {data.file && (
                        <div className="flex flex-col gap-2 w-full h-fit mt-2 pb-2">
                          <div className="w-full">
                            <FilePreview
                              file={data.file}
                              onRemove={handleFileRemove}
                              disabled={processing}
                              iconColor="text-green-600"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Solo se permite un archivo Excel (.xlsx, .xls) o CSV
                      (.csv)
                    </p>
                    <InputError message={errors.file} />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={processing || !data.file}
                    tabIndex={3}
                    onClick={(e) => {
                      if (
                        !window.confirm(
                          "Esto procesará el archivo e importará los productos. ¿Continuar?",
                        )
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {processing && (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    )}
                    Importar Productos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.history.back()}
                    disabled={processing}
                    tabIndex={4}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
